// Excel export of the Cobra-style EVM table in Cesar's layout (his Oct 20 "Cobra" and "UOI" workbooks): formula
// labels across the top, yellow input data, grey calculated cells, and the Cesar Add-On block in green with a red
// border. Calculated columns are real Excel formulas over the row's own cells, with the computed results cached.
import ExcelJS from "exceljs";
import { COBRA_COLUMNS, FORMULAS, type CobraRow, type CobraTable, type CobraValues, type EacBasis } from "./cobra";

const FILL = { data: "FFFFFFCC", calc: "FFEDEDED", addon: "FFE2EFDA", head: "FFF2F2F2", input: "FFFFFF00" };
const RED = "FFFF0000";
const FIRST_COL = 3; // A = margin, B = labels, data from C
const GAP_BEFORE = "spi"; // narrow spacer column between the Cobra table and the Cesar Add-On

interface Layout {
  col: Partial<Record<keyof CobraValues, number>>;
  gap: number;
  last: number;
}

function layout(): Layout {
  const col: Layout["col"] = {};
  let c = FIRST_COL;
  let gap = 0;
  for (const k of COBRA_COLUMNS) {
    if (k.key === GAP_BEFORE) gap = c++;
    col[k.key] = c++;
  }
  return { col, gap, last: c - 1 };
}

const letter = (n: number) => {
  let s = "";
  for (; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
};

export async function writeCobraWorkbook(t: CobraTable, meta: { title: string; project: string; period: string; cutoff: Date; unit: string; eac: EacBasis; rowsLabel: string }): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Mongoose EVM";
  const ws = wb.addWorksheet("Progress Table", {
    pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 } },
    views: [{ state: "frozen", xSplit: 2, ySplit: 9 }],
  });
  const L = layout();
  const ref = (k: keyof CobraValues, r: number) => `${letter(L.col[k]!)}${r}`;
  const font = { name: "Calibri", size: 10 };
  ws.getColumn(1).width = 1.5;
  ws.getColumn(2).width = 40;
  for (const c of COBRA_COLUMNS) ws.getColumn(L.col[c.key]!).width = c.tone === "addon" ? 11.5 : 12;
  ws.getColumn(L.gap).width = 1.6;
  const addonCols = COBRA_COLUMNS.filter((c) => c.tone === "addon").map((c) => L.col[c.key]!);
  const firstAddon = Math.min(...addonCols);

  // Title block
  const put = (r: number, c: number, v: ExcelJS.CellValue, f: Partial<ExcelJS.Style> = {}) => {
    const cell = ws.getCell(r, c);
    cell.value = v;
    cell.font = { ...font, ...(f.font ?? {}) };
    if (f.alignment) cell.alignment = f.alignment;
    if (f.fill) cell.fill = f.fill;
    if (f.numFmt) cell.numFmt = f.numFmt;
    if (f.border) cell.border = f.border;
    return cell;
  };
  const solid = (argb: string): ExcelJS.Fill => ({ type: "pattern", pattern: "solid", fgColor: { argb } });
  put(1, 2, `MONGOOSE EVM PROGRESS REPORT – ${meta.title.toUpperCase()}`, { font: { ...font, bold: true, size: 12 } });
  put(2, 2, "Status Update", { font: { ...font, bold: true }, alignment: { horizontal: "right" } });
  put(2, 3, meta.cutoff, { font: { ...font, bold: true }, fill: solid(FILL.input), numFmt: "d-mmm-yy", alignment: { horizontal: "center" } });
  put(3, 2, "Period", { font: { ...font, bold: true }, alignment: { horizontal: "right" } });
  put(3, 3, meta.period, { font: { ...font, bold: true }, fill: solid(FILL.input), alignment: { horizontal: "center" } });
  put(4, 2, "Work Hours in Period", { font: { ...font, bold: true }, alignment: { horizontal: "right" } });
  put(4, 3, t.stdHours, { font: { ...font, bold: true }, fill: solid(FILL.input), alignment: { horizontal: "center" } });
  put(2, 5, `Project: ${meta.project} · ${meta.unit === "$" ? "dollars" : "hours"}`, { font: { ...font, bold: true } });
  put(2, firstAddon, "Mongoose EVM Add-On", { font: { ...font, bold: true, color: { argb: RED } } });
  put(3, firstAddon, "Not in the standard EVM table", { font: { ...font, italic: true, color: { argb: RED }, size: 9 } });
  put(2, L.col.pctComplete!, "", { fill: solid(FILL.calc) });
  put(2, L.col.pctComplete! + 1, " = calculated (formula) cells", { font: { ...font, size: 9 } });

  // Header: formula tags (row 5), group names (6), two header lines (7, 8), blank band (9)
  put(8, 2, meta.rowsLabel, { font: { ...font, bold: true }, alignment: { vertical: "bottom" } });
  for (const r of [6, 7, 8]) ws.getCell(r, 2).border = { left: { style: "medium" }, ...(r === 6 ? { top: { style: "medium" } } : {}), ...(r === 8 ? { bottom: { style: "medium" } } : {}) };
  let prevGroup = "";
  for (const c of COBRA_COLUMNS) {
    const n = L.col[c.key]!;
    put(5, n, c.tag, { font: { ...font, size: 8, italic: true }, alignment: { horizontal: "center" } });
    put(6, n, c.group === prevGroup ? "" : c.group, { font: { ...font, bold: true }, alignment: { horizontal: "center" } });
    put(7, n, c.key === "tcpiEac" ? (meta.eac === "eac1" ? "Perf. to EAC1" : "Perf. to Basic EAC") : c.head[0], { font: { ...font, bold: true }, alignment: { horizontal: "center" } });
    put(8, n, c.head[1], { font: { ...font, bold: true }, alignment: { horizontal: "center" } });
    for (const r of [6, 7, 8]) {
      const cell = ws.getCell(r, n);
      cell.fill = solid(c.tone === "addon" ? FILL.addon : FILL.head);
      cell.border = c.tone === "addon" ? { left: { style: "medium", color: { argb: RED } }, right: { style: "medium", color: { argb: RED } }, ...(r === 6 ? { top: { style: "medium", color: { argb: RED } } } : {}), ...(r === 8 ? { bottom: { style: "thin" } } : {}) } : { ...(r === 6 ? { top: { style: "medium" } } : {}), ...(r === 8 ? { bottom: { style: "medium" } } : {}), ...(c.group !== prevGroup ? { left: { style: "thin" } } : {}) };
    }
    prevGroup = c.group;
  }

  // Body
  let r = 10;
  const fmtOf = (f: string) => (f === "pct" ? "0.0%" : f === "idx" ? "0.00" : f === "hc" ? "0.0" : "#,##0");
  const formulaFor = (k: keyof CobraValues, row: number): string | null => {
    const R = (x: keyof CobraValues) => ref(x, row);
    const eac = meta.eac === "eac1" ? R("eac1") : R("eacBasic");
    switch (k) {
      case "pctExpended": return `IFERROR(${R("ac")}/${R("bac")},0)`;
      case "pctPeriod": return `IFERROR(${R("evPeriod")}/${R("bac")},0)`;
      case "pctComplete": return `IFERROR(${R("ev")}/${R("bac")},0)`;
      case "pctPlanned": return `IFERROR(${R("pv")}/${R("bac")},0)`;
      case "pfPeriod": return `IFERROR(${R("evPeriod")}/${R("acPeriod")},"")`;
      case "pf": return `IFERROR(${R("ev")}/${R("ac")},"")`;
      case "craft": return `IFERROR(${R("acPeriod")}/$C$4,"")`;
      case "spi": return `IFERROR(${R("ev")}/${R("pv")},"")`;
      case "csi": return `IFERROR(${R("spi")}*${R("pf")},"")`;
      case "eac1": return `IFERROR((${R("bac")}-${R("ev")})/${R("pf")}+${R("ac")},"")`;
      case "eacBasic": return `${R("bac")}-${R("ev")}+${R("ac")}`;
      case "vac": return `IFERROR(${R("bac")}-${R("eac1")},"")`;
      case "tcpi": return `IFERROR((${R("bac")}-${R("ev")})/(${R("bac")}-${R("ac")}),"")`;
      case "tcpiEac": return `IFERROR((${R("bac")}-${R("ev")})/(${eac}-${R("ac")}),"")`;
      case "remainingEarn": return `${R("bac")}-${R("ev")}`;
      case "remainingBurn": return `${R("bac")}-${R("ac")}`;
      default: return null;
    }
  };
  const writeRow = (row: CobraRow) => {
    const strong = row.kind !== "row" && row.kind !== "indirect";
    put(r, 2, row.label, { font: { ...font, bold: strong }, alignment: { horizontal: strong ? "right" : "left", indent: row.kind === "row" || row.kind === "indirect" ? 1 : 0 } });
    for (const c of COBRA_COLUMNS) {
      const n = L.col[c.key]!;
      const v = row.v[c.key];
      const result = v === null || (typeof v === "number" && !isFinite(v)) ? "" : v;
      const f = formulaFor(c.key, r);
      const cell = ws.getCell(r, n);
      cell.value = f ? { formula: f, result: result as number } : (result as number);
      cell.font = { ...font, bold: strong, ...(c.key === "vac" && typeof v === "number" && v < 0 ? { color: { argb: RED } } : {}) };
      cell.numFmt = fmtOf(c.fmt);
      cell.alignment = { horizontal: "right" };
      const tone = c.tone === "addon" ? FILL.addon : f ? FILL.calc : c.tone === "data" && row.kind === "row" ? FILL.data : null;
      if (tone) cell.fill = solid(tone);
      cell.border = {
        bottom: { style: strong ? "thin" : "dashed", color: { argb: "FF7F7F7F" } },
        ...(strong ? { top: { style: "thin" } } : {}),
        ...(c.tone === "addon" ? { left: { style: "medium", color: { argb: RED } }, right: { style: "medium", color: { argb: RED } } } : {}),
      };
    }
    ws.getCell(r, 2).border = { left: { style: "medium" }, bottom: { style: strong ? "thin" : "dashed", color: { argb: "FF7F7F7F" } }, ...(strong ? { top: { style: "thin" } } : {}) };
    r++;
  };
  const sectionHead = (label: string) => {
    put(r, 2, label, { font: { ...font, bold: true, underline: true } });
    ws.getCell(r, 2).border = { left: { style: "medium" } };
    for (const n of addonCols) ws.getCell(r, n).border = { left: { style: "medium", color: { argb: RED } }, right: { style: "medium", color: { argb: RED } } };
    r++;
  };
  for (const s of t.sections) {
    if (s.label) sectionHead(s.label);
    s.rows.forEach(writeRow);
    if (s.subtotal) writeRow(s.subtotal);
  }
  writeRow(t.total);
  if (t.indirect) {
    sectionHead("Indirects");
    t.indirect.rows.forEach(writeRow);
    writeRow(t.indirect.grand);
  }
  // Close the frames
  ws.getCell(r - 1, 2).border = { ...ws.getCell(r - 1, 2).border, bottom: { style: "medium" } };
  for (const n of addonCols) ws.getCell(r - 1, n).border = { ...ws.getCell(r - 1, n).border, bottom: { style: "medium", color: { argb: RED } } };

  // Notes and formula legend
  r++;
  put(r++, 2, "Every row, subtotal and total computes its own indices and EAC from its own sums; totals are never added up from row indices.", { font: { ...font, color: { argb: RED }, bold: true } });
  put(r++, 2, `TCPI EAC uses ${meta.eac === "eac1" ? "EAC1" : "EAC Basic"}. A Performance Factor above 1.00 is favourable. A negative VAC is an overrun.`, { font: { ...font, color: { argb: RED } } });
  r++;
  put(r, 2, "EARNED VALUE CALCULATIONS (ANSI-748)", { font: { ...font, bold: true, underline: true } });
  r++;
  for (const [a, b] of FORMULAS) {
    put(r, 2, a, {});
    put(r, 3, b, {});
    r++;
  }
  return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
}
